import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface Props {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  accent?: string;
  noPadding?: boolean;
  actions?: ReactNode;
}

/** Dashboard section headers also resolve through Translator Master. */
export default function DashboardSection({
  title,
  subtitle,
  children,
  accent = '#2563eb',
  noPadding,
  actions,
}: Props) {
  const { t } = useI18n();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const resolvedTitle = title ? translateUiText(title, t) : undefined;
  const resolvedSubtitle = subtitle ? translateUiText(subtitle, t) : undefined;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        mb: 3,
        borderRadius: '16px',
        border: `1px solid ${isLight ? alpha('#e2e8f0', 0.8) : alpha('#1e2d4a', 0.6)}`,
        overflow: 'hidden',
      }}
    >
      {(resolvedTitle || resolvedSubtitle) && (
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 1.75,
            borderBottom: `1px solid ${isLight ? alpha('#e2e8f0', 0.7) : alpha('#1e2d4a', 0.6)}`,
            bgcolor: isLight ? alpha(accent, 0.035) : alpha(accent, 0.06),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            {resolvedTitle && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 3,
                  height: 16,
                  borderRadius: '2px',
                  bgcolor: accent,
                  flexShrink: 0,
                }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{ letterSpacing: '-0.01em', fontSize: '0.9rem' }}
                >
                  {resolvedTitle}
                </Typography>
              </Box>
            )}
            {resolvedSubtitle && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.25} sx={{ fontSize: '0.75rem', ml: '11px' }}>
                {resolvedSubtitle}
              </Typography>
            )}
          </Box>
          {actions && <Box>{actions}</Box>}
        </Box>
      )}
      <Box sx={{ p: noPadding ? 0 : { xs: 2, md: 2.5 }, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
        {children}
      </Box>
    </Paper>
  );
}
